import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { enableMapSet } from 'immer';
import { readConfiguration, readSalesText, readProductImage } from '../../api/ConfigurationApi';
import { IConfigurationState, IImagesData, ISalesText } from '../../../types';
import {
  IReadConfigurationRequest,
  IReadImageRequest,
  IReadSalesTextRequest,
} from '../../../types/IApiTypes';
import {
  addFeatures,
  getSectionRows,
  setProductImages,
  updateShortSalesText,
} from '../../store/Helper/configurationSliceHelper';

enableMapSet();

const initialState: IConfigurationState = {
  data: null,
  searchValue: '',
  configurations: [],
  configurationId: null,
  productId: '',
  packagePath: '',
  subModels: [],
  shortSalesText: new Map<string, Map<string, ISalesText>>(),
  longSalesText: new Map<string, Map<string, ISalesText>>(),
  images: new Map<string, Map<string, IImagesData>>(),
};

//Get the views available for configuration id
export const getConfigurationData = createAsyncThunk(
  'configuration/getConfigurationData',
  async (action: IReadConfigurationRequest) => {
    return readConfiguration(action);
  }
);

//Get the salestext available for product id
export const getSalesText = createAsyncThunk(
  'configuration/getSalesText',
  async (action: IReadSalesTextRequest) => {
    return readSalesText(action);
  }
);

//Get the images available for product id
export const getImages = createAsyncThunk(
  'configuration/getImages',
  async (action: IReadImageRequest) => {
    return readProductImage(action);
  }
);

const configurationSlice = createSlice({
  name: 'configuration',
  initialState,
  reducers: {
    addConfiguration(state, action) {
      state.configurations.push({
        configurationId: action.payload.configurationId,
        createdOn: action.payload.createdOn,
        modifiedOn: action.payload.modifiedOn,
        productId: action.payload.productInfo.id,
        productName: action.payload.productInfo.description,
        isProductActive: action.payload.productInfo.isActive,
        countryName: action.payload.productInfo.market.code,
        countryCode: action.payload.productInfo.market.name,
        views: action.payload.productInfo.views,
        isValid: action.payload.validationResult.isValid,
        configureRequest: action.payload.userAssignments,
      });
    },
    resetConfiguration(state) {
      state.data = null;
      state.configurationId = null;
    },
    updateDiffFeature(state, action) {
      state.data = state.data?.concat(addFeatures(action.payload, state.data?.length));
    },
    setSearchValue(state, action) {
      state.searchValue = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getConfigurationData.fulfilled, (state, action) => {
      if (action.payload) {
        const params = action.meta.arg;
        const productId = action.payload.product.id;
        const rows = [];
        const subModels: string[] = [];

        rows.push({
          id: 0,
          sectionName: productId,
          productId: productId,
          isProduct: true,
          isSection: true,
        });

        getSectionRows(state, action.payload?.sections, rows, subModels, false);
        state.configurationId = params.configurationId;
        state.data = rows;
        state.productId = productId;
        state.packagePath = action.payload.packagePath;
        state.subModels = subModels;
      }
    });

    builder.addCase(getSalesText.fulfilled, (state, action) => {
      if (action.payload) {
        updateShortSalesText(action.payload, state);
      }
    });

    builder.addCase(getImages.fulfilled, (state, action) => {
      if (action.payload) {
        setProductImages(action.payload, state);
      }
    });
  },
});

export const { addConfiguration, resetConfiguration, updateDiffFeature, setSearchValue } =
  configurationSlice.actions;

export default configurationSlice.reducer;
